/** Default sort used by the assessments list screen (client-side only). */
export const ASSESSMENT_LIST_DEFAULTS = {
  size: 10,
  sortBy: 'name',
  sortDir: 'asc',
};

/** Sort keys exposed by the assessments table column headers. */
export const ASSESSMENT_SORT_FIELDS = {
  name: 'name',
  courseName: 'courseName',
  startDate: 'startDate',
  endDate: 'endDate',
  active: 'active',
};

/**
 * Computes the next client-side sort when a column header is clicked.
 * Same column toggles asc/desc; a new column always starts asc.
 */
export function getNextAssessmentSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || ASSESSMENT_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return {
      sortBy,
      sortDir: currentSortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy, sortDir: 'asc' };
}

/**
 * Stable cache key for a paginated assessment list query.
 * Keyed by userId + page + size + keyword + courseId (sort is client-side only).
 */
export function buildAssessmentListCacheKey(userId, {
  page = 0,
  size = ASSESSMENT_LIST_DEFAULTS.size,
  keyword = '',
  courseId = '',
} = {}) {
  const kw = (keyword || '').trim();
  const cid = (courseId || '').trim();
  return `assessments:${userId}:${page}:${size}:${kw}:${cid || 'all'}`;
}

/**
 * Builds query string for paginated assessment list API calls.
 * Sorting is applied client-side; the API is not called on column-header clicks.
 */
export function buildAssessmentListQuery({
  page = 0,
  size = 10,
  keyword = '',
  courseId = '',
} = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  if (keyword && keyword.trim()) {
    params.set('keyword', keyword.trim());
  }
  if (courseId && String(courseId).trim()) {
    params.set('courseId', String(courseId).trim());
  }
  return params.toString();
}

function parseDateMs(value) {
  if (!value) return 0;
  const n = Number(value);
  if (!Number.isNaN(n) && n > 0) return n;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/** Resolves a row field value for client-side column sorting. */
function assessmentSortValue(row, sortBy) {
  switch (sortBy) {
    case ASSESSMENT_SORT_FIELDS.courseName:
      return row.courseName ?? '';
    case ASSESSMENT_SORT_FIELDS.startDate:
      return parseDateMs(row.startDate);
    case ASSESSMENT_SORT_FIELDS.endDate:
      return parseDateMs(row.endDate);
    case ASSESSMENT_SORT_FIELDS.active:
      return row.active ?? 0;
    case ASSESSMENT_SORT_FIELDS.name:
    default:
      return row.name ?? '';
  }
}

/**
 * Sorts the current page of assessment rows in memory — no API round-trip.
 */
export function sortAssessmentsLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || ASSESSMENT_LIST_DEFAULTS.sortBy;

  list.sort((a, b) => {
    const av = assessmentSortValue(a, field);
    const bv = assessmentSortValue(b, field);
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}

/**
 * Parses paginated assessment list API response; falls back to array payloads for legacy callers.
 */
export function parseAssessmentListResponse(payload) {
  if (Array.isArray(payload)) {
    return {
      content: payload,
      totalElements: payload.length,
      totalPages: 1,
      page: 0,
      size: payload.length,
    };
  }

  return {
    content: Array.isArray(payload?.content) ? payload.content : [],
    totalElements: Number(payload?.totalElements ?? 0),
    totalPages: Number(payload?.totalPages ?? 0),
    page: Number(payload?.page ?? 0),
    size: Number(payload?.size ?? 10),
  };
}
